import mixpanel from 'mixpanel-browser';

declare const __APP_ENV__: string;
declare const __MIXPANEL_TOKEN__: string | undefined;
declare const __MIXPANEL_API_HOST__: string | undefined;

let initialized = false;
let lastIdentifiedUserId: string | null = null;

export type Persona = 'assist' | 'self_care';

const ASSIST_ROLES = new Set(['tenant-admin', 'tenant-owner', 'partner-admin', 'platform-admin']);

function inferPersonaFromRoles(roles?: string[]): Persona {
  if (!roles) return 'self_care';
  return roles.some((r) => ASSIST_ROLES.has(r)) ? 'assist' : 'self_care';
}

/**
 * Set the persona super-property so all subsequent events carry it.
 * Call this whenever the active persona changes (e.g. route-based detection).
 */
export function setPersona(persona: Persona): void {
  if (!initialized) return;
  mixpanel.register({ persona });
}

/**
 * Initialize Mixpanel for product analytics, autocapture, and session replay.
 * Enabled in all environments when a token is configured.
 * Set MIXPANEL_API_HOST to https://api-eu.mixpanel.com for EU-hosted projects.
 */
export function initMixpanel(): void {
  if (!__MIXPANEL_TOKEN__) {
    return;
  }

  mixpanel.init(__MIXPANEL_TOKEN__, {
    debug: __APP_ENV__ !== 'production',
    api_host: __MIXPANEL_API_HOST__ || 'https://api-js.mixpanel.com',
    persistence: 'localStorage',
    ignore_dnt: true,
    autocapture: {
      pageview: 'url-with-path',
      click: true,
      input: true,
      scroll: true,
      submit: true,
      capture_text_content: false,
    },
    record_sessions_percent: 100,
    record_heatmap_data: true,
    loaded: (mp) => {
      try {
        // eslint-disable-next-line no-console
        console.log('[Mixpanel] Loaded. distinct_id=', mp.get_distinct_id());
        mp.track('App Loaded', {
          environment: __APP_ENV__,
          source: 'app-frontend',
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[Mixpanel] loaded callback failed', err);
      }
    },
  });

  initialized = true;
}

/**
 * Identify a user in Mixpanel after successful authentication.
 * Call this when the user session is established.
 */
export function identifyUser(userId: string, properties?: {
  email?: string;
  name?: string;
  tenantId?: string;
  tenantName?: string;
  roles?: string[];
  preferredLanguage?: string | null;
}): void {
  if (!initialized) return;

  const isFirstIdentifyForUser = lastIdentifiedUserId !== userId;
  mixpanel.identify(userId);
  lastIdentifiedUserId = userId;

  if (properties) {
    const superProps: Record<string, unknown> = {};
    if (properties.tenantId) superProps.tenant_id = properties.tenantId;
    if (properties.tenantName) superProps.tenant_name = properties.tenantName;
    if (properties.roles) superProps.roles = properties.roles;
    if (properties.preferredLanguage) superProps.preferred_language = properties.preferredLanguage;
    superProps.persona = inferPersonaFromRoles(properties.roles);
    mixpanel.register(superProps);

    const displayName = properties.name && properties.tenantName
      ? `[${properties.tenantName}] ${properties.name}`
      : properties.name;

    mixpanel.people.set({
      ...(properties.email && { $email: properties.email }),
      ...(displayName && { $name: displayName }),
      ...superProps,
    });

    // Fire an explicit event so the identified user has at least one tracked
    // event tied to their user_id — otherwise the Users view may not surface
    // a profile that only has $set data and no events. Only on first identify
    // per user per page load to avoid duplicate events on re-mounts.
    if (isFirstIdentifyForUser) {
      mixpanel.track('User Identified', {
        user_id: userId,
        ...(properties.email && { email: properties.email }),
        ...(properties.tenantId && { tenant_id: properties.tenantId }),
      });
    }
  }
}

/**
 * Track a custom event in Mixpanel.
 */
export function trackEvent(eventName: string, properties?: Record<string, unknown>): void {
  if (!initialized) return;

  mixpanel.track(eventName, properties);
}

/**
 * Stop Mixpanel session recording — call this on unauthenticated pages
 * (login, OTP entry) to prevent keystroke capture on auth form fields.
 * GDPR Art. 25: data minimisation — don't record what you don't need.
 */
export function stopSessionRecording(): void {
  if (!initialized) return;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (mixpanel as any).stop_session_recording?.();
}

/**
 * Resume Mixpanel session recording after the user has authenticated.
 */
export function startSessionRecording(): void {
  if (!initialized) return;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (mixpanel as any).start_session_recording?.();
}

/**
 * Reset Mixpanel identity on logout.
 */
export function resetMixpanel(): void {
  if (!initialized) return;

  mixpanel.reset();
  lastIdentifiedUserId = null;
}
